import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';

import {
    Alert, Box, ButtonGroup, Chip, CircularProgress, Collapse, Grid,
    IconButton, Stack, TableCell, TableRow, Tooltip, Typography
} from '@mui/material';
import ActionButton from '../../lib/ActionButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LockIcon from '@mui/icons-material/Lock';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';

import { Item, isOwner } from '../../lib/datasetUI';
import { formatApiError } from '../../lib/formatApiError';
import { getLogo, printOrgName } from '../../lib/styled';
import {
    useGetMqttStatusQuery,
    useCreateMqttSetupMutation,
    useUpdateMqttSetupMutation,
    useDeleteMqttSetupMutation
} from '../../state/apiSlice';

import MqttInfo from './MqttInfo';
import MqttSetupDialog from './MqttSetupDialog';
import ConfirmActionDialog from './ConfirmActionDialog';

/**
 * At-a-glance MQTT badge — visible whether the row is collapsed or
 * expanded, so admins can scan a list of datasets without expanding
 * each one and still see the live status while reading the panel.
 * States:
 *   - loading            → "MQTT: loading…"
 *   - 404 (not set up)   → "MQTT: not set up" (muted)
 *   - other error        → "MQTT: error"
 *   - data present       → "MQTT: <helmStatus>" (success/warning by phase)
 *
 * The fetch is eager (no skip), since the chip's value is the whole
 * point of the column. RTK Query dedupes by tag so we pay one request
 * per row across the page lifetime.
 */
/**
 * RTK Query's fetchBaseQuery exposes the HTTP status as a number on
 * `error.status` for normal responses, but reuses that field for the
 * sentinel strings 'FETCH_ERROR' / 'PARSING_ERROR' / 'CUSTOM_ERROR' when
 * the response can't be parsed (our /mqtt/status 404 sends plain text,
 * tripping PARSING_ERROR). The real HTTP code in those cases lives on
 * `originalStatus`. A single helper keeps the panel and the chip from
 * drifting again.
 */
function httpStatusOf(err) {
    if (!err) return undefined;
    return typeof err.status === 'number' ? err.status : err.originalStatus;
}

function MqttStatusChip({ status, isFetching, isError, error }) {
    if (isFetching) {
        return <Chip size="small" variant="outlined" label="MQTT: loading…" />;
    }
    // 404 → "not set up", any other error → "error".
    if (isError && httpStatusOf(error) !== 404) {
        return <Chip size="small" color="error" icon={<ErrorIcon />} variant="outlined" label="MQTT: error" />;
    }
    if (!status) {
        return <Chip size="small" variant="outlined" sx={{ opacity: 0.6 }} label="MQTT: not set up" />;
    }
    // helm status -o json puts the lifecycle string under info.status
    // ("deployed", "failed", ...). The error paths in helmClient.js still
    // emit a flat { status: 'not_found' | 'unknown' }, so honour both.
    const helmStatus = status.helm?.info?.status || status.helm?.status || status.status || 'running';
    const ok = ['deployed', 'running', 'ok'].includes(String(helmStatus).toLowerCase());
    return (
        <Chip
            size="small"
            color={ok ? 'success' : 'warning'}
            icon={ok ? <CheckCircleIcon /> : <ErrorIcon />}
            variant="outlined"
            label={`MQTT: ${helmStatus}`}
        />
    );
}

MqttStatusChip.propTypes = {
    status: PropTypes.object,
    isFetching: PropTypes.bool,
    isError: PropTypes.bool,
    error: PropTypes.object
};

/**
 * Foldable row showing dataset summary, with an unfolded admin panel containing
 * dataset metadata and MQTT setup info + actions.
 *
 * Reuses the visual language of DatasetCatalog (table row + Collapse) and the
 * Item card from Inspector, so the page feels cohesive.
 *
 * Accessibility: the toggle is an explicit chevron <IconButton> (DES1) so
 * keyboard users can focus and Enter/Space to open. The row itself is no
 * longer clickable, and inner buttons no longer need stopPropagation.
 *
 * Props:
 * - ds: dataset shape (DatasetID, DatasetName, ...)
 * - mode?: 'admin' | 'user' — gates the action surface. 'admin' (default)
 *   renders Set up / Edit / Delete + the corresponding dialogs. 'user'
 *   hides those entirely; the panel becomes read-only (MqttInfo, refresh
 *   button, "Open in Overview" only). The chip column, expanded panel
 *   and refresh control are identical in both modes.
 * - onEditMetadata(ds): called when user clicks the "Open in Overview" link.
 *   Typical caller behaviour: select the dataset and switch to the Overview tab.
 * - onMqttSuccess?(verb): fired on successful MQTT create/edit/delete; the
 *   verb argument is one of 'created'|'updated'|'deleted' for snackbar copy.
 *   Only invoked in 'admin' mode.
 */
export default function AdminDatasetRow({ ds, mode = 'admin', onEditMetadata, onMqttSuccess }) {
    const isAdminMode = mode === 'admin';
    const [open, setOpen] = useState(false);
    const [mqttDialogOpen, setMqttDialogOpen] = useState(false);
    const [deleteMqttOpen, setDeleteMqttOpen] = useState(false);

    // Fetch MQTT status eagerly so the row header can show "MQTT: not
    // set up" / "MQTT: deployed" before the row is expanded — the badge
    // is the most useful at-a-glance signal for an admin scanning a
    // long list. RTK Query dedupes by tag id, so N rows fan out to N
    // requests once and then share the cache. Refetch is exposed so the
    // panel header can offer a manual refresh (useful after a Helm
    // rollout finishes), since RTK Query only auto-invalidates on our
    // own mutations.
    const {
        data: mqttStatus,
        isLoading: mqttIsLoading,
        isFetching: mqttFetching,
        isError: mqttIsError,
        error: mqttError,
        refetch: refetchMqtt
    } = useGetMqttStatusQuery(ds.DatasetID);

    const [createMqtt] = useCreateMqttSetupMutation();
    const [updateMqtt] = useUpdateMqttSetupMutation();
    const [deleteMqtt] = useDeleteMqttSetupMutation();

    // Determine MQTT presence: 404 from backend → not set up. Other errors → real errors.
    // See httpStatusOf() above for the PARSING_ERROR→originalStatus rationale.
    const mqttNotFound = mqttIsError && httpStatusOf(mqttError) === 404;
    const mqttHasSetup = !!mqttStatus && !mqttNotFound;
    const mqttGenuineError = mqttIsError && !mqttNotFound;

    const handleMqttSubmit = async (body) => {
        if (mqttHasSetup) {
            await updateMqtt({ datasetid: ds.DatasetID, body }).unwrap();
        } else {
            await createMqtt({ datasetid: ds.DatasetID, body }).unwrap();
        }
        // The mutation declares invalidatesTags: [{type: 'MQTTStatus', id}]
        // (apiSlice.js), so RTK Query refetches automatically. No explicit
        // refetch() needed — doing both fires a redundant request.
    };

    const handleMqttDelete = async () => {
        await deleteMqtt({ datasetid: ds.DatasetID }).unwrap();
        // Same reasoning as handleMqttSubmit — invalidatesTags refetches.
    };

    const toggleOpen = () => setOpen(o => !o);

    // User-mode list filter. Non-admin viewers ('user' mode) only care
    // about datasets they can actually connect to via MQTT — a row that
    // says "MQTT: not set up" is just noise to them. We self-hide here
    // because the row component is the only place that owns the
    // /mqtt/status query result; pushing this filter up to Admin.js
    // would mean lifting N parallel queries to the parent and losing
    // the row's self-contained data flow.
    //
    // Layout note: while the status query is in flight we render `null`
    // immediately rather than a placeholder skeleton row. RTK Query
    // resolves /mqtt/status in one round-trip per dataset (deduped by
    // tag), and the cache hit on a re-render is synchronous. The
    // visible "thinning" of the table as 404s come back is a single
    // render pass per row, not an animation; a placeholder would add
    // its own jank by inserting then removing a row. If a future
    // perf regression makes this visible, swap the `return null` below
    // for a SkeletonRow import.
    const hideForUser = mode === 'user' && !mqttIsLoading && !mqttHasSetup;
    if (hideForUser) {
        return null;
    }

    return (
        <Fragment>
            <TableRow hover data-testid={`admin-row-${ds.DatasetID}`}>
                <TableCell padding="checkbox" align="center">
                    <IconButton
                        size="small"
                        onClick={toggleOpen}
                        aria-expanded={open}
                        aria-controls={`admin-row-detail-${ds.DatasetID}`}
                        aria-label={open
                            ? `Collapse details for ${ds.DatasetName}`
                            : `Expand details for ${ds.DatasetName}`}
                        data-testid={`admin-row-toggle-${ds.DatasetID}`}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell padding="checkbox" align="center">
                    <img src={getLogo(ds.Organization)} alt={`${ds.Organization} logo`}
                        style={{ height: 32, maxWidth: 80, objectFit: 'contain' }} />
                </TableCell>
                <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{ds.DatasetName}</Typography>
                </TableCell>
                <TableCell>{printOrgName(ds.Organization)}</TableCell>
                <TableCell>
                    <Chip
                        size="small"
                        variant="outlined"
                        color={isOwner(ds) ? 'primary' : 'default'}
                        label={ds.AccessRole}
                    />
                </TableCell>
                <TableCell>
                    {ds.AccessType === 'open'
                        ? <Chip size="small" icon={<Diversity3Icon />} variant="outlined" label="Open" />
                        : <Chip size="small" icon={<LockIcon />} variant="outlined" label="Restricted" />}
                </TableCell>
                <TableCell>
                    <MqttStatusChip
                        status={mqttHasSetup ? mqttStatus : null}
                        isFetching={mqttFetching}
                        isError={mqttIsError}
                        error={mqttError}
                    />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={open} timeout={200} unmountOnExit>
                        <Box id={`admin-row-detail-${ds.DatasetID}`} sx={{ py: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <Item>
                                        <Typography variant="h6">Dataset</Typography>
                                        <Stack direction="row" spacing={0.5}
                                            sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                                            {(ds.Tags || []).map(tag => (
                                                <Chip key={tag} size="small" variant="outlined" label={tag} />
                                            ))}
                                        </Stack>
                                        <Box sx={{ mt: 2 }}>
                                            <Tooltip title="Open this dataset in the Overview tab to edit metadata, roles and access requests.">
                                                <ActionButton
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<OpenInNewIcon />}
                                                    endIcon={<EditIcon fontSize="small" />}
                                                    onClick={() => onEditMetadata(ds)}
                                                >
                                                    Open in Overview
                                                </ActionButton>
                                            </Tooltip>
                                        </Box>
                                    </Item>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <Item>
                                        {/*
                                          Header row: heading on the left,
                                          refresh on the right (ml: 'auto').
                                          Refresh stays secondary — small
                                          IconButton, no label — so the "MQTT"
                                          heading remains the visual anchor.
                                          The spin animation only runs while
                                          fetching, mirroring the panel's
                                          first-load CircularProgress.
                                        */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <SettingsRemoteIcon />
                                            <Typography variant="h6">MQTT</Typography>
                                            <Tooltip title="Refresh MQTT status">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => refetchMqtt()}
                                                    disabled={mqttFetching}
                                                    aria-label="Refresh MQTT status"
                                                    sx={{
                                                        ml: 'auto',
                                                        '@keyframes mqttRefreshSpin': {
                                                            from: { transform: 'rotate(0deg)' },
                                                            to: { transform: 'rotate(360deg)' }
                                                        },
                                                        '& svg': mqttFetching ? {
                                                            animation: 'mqttRefreshSpin 1s linear infinite'
                                                        } : undefined
                                                    }}
                                                >
                                                    <RefreshIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        {/*
                                          Layout-stable refresh: gate the big
                                          spinner block on `isLoading` (first
                                          load only), not `isFetching` (any
                                          in-flight). On manual refresh the
                                          previously-rendered block (Alert /
                                          empty-state copy / MqttInfo) stays
                                          mounted, so the panel height — and
                                          the surrounding table rows — don't
                                          jump. The spinning RefreshIcon in
                                          the header above carries the "I'm
                                          reloading" feedback by itself.
                                        */}
                                        {mqttIsLoading && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                                role="status" aria-live="polite">
                                                <CircularProgress size={20} />
                                                <Typography variant="body2">Loading MQTT status…</Typography>
                                            </Box>
                                        )}
                                        {!mqttIsLoading && mqttGenuineError && (
                                            <Alert severity="error" sx={{ mt: 1 }}>
                                                Failed to fetch MQTT status: {formatApiError(mqttError, 'unknown error')}
                                            </Alert>
                                        )}
                                        {!mqttIsLoading && !mqttGenuineError && !mqttHasSetup && (
                                            <Typography variant="body2" color="text.secondary">
                                                No MQTT setup for this dataset.
                                            </Typography>
                                        )}
                                        {!mqttIsLoading && mqttHasSetup && (
                                            <MqttInfo status={mqttStatus} datasetId={ds.DatasetID} mode={mode} />
                                        )}
                                        {/*
                                          Action surface is admin-only.
                                          Read-only viewers ('user' mode)
                                          see the MqttInfo panel above
                                          but no Set up / Edit / Delete
                                          buttons — they wouldn't be
                                          allowed to use them anyway, and
                                          surfacing them as
                                          "permission-denied on click"
                                          would be a worse UX than just
                                          hiding the affordance.
                                        */}
                                        {isAdminMode && (
                                            <ButtonGroup
                                                sx={{ mt: 2, flexWrap: 'wrap' }}
                                                variant="outlined"
                                            >
                                                <ActionButton
                                                    startIcon={<SettingsRemoteIcon />}
                                                    onClick={() => setMqttDialogOpen(true)}
                                                >
                                                    {mqttHasSetup ? 'Edit MQTT setup' : 'Set up MQTT'}
                                                </ActionButton>
                                                {mqttHasSetup && (
                                                    <Tooltip title={isOwner(ds)
                                                        ? 'Permanently remove MQTT setup'
                                                        : 'Only the dataset owner can delete the MQTT setup'}>
                                                        {/*
                                                          Tooltip needs a focusable
                                                          child to fire on
                                                          keyboard focus when the
                                                          Button is disabled
                                                          (DES19). The wrapper
                                                          span carries tabIndex.
                                                        */}
                                                        <span tabIndex={isOwner(ds) ? -1 : 0}>
                                                            <ActionButton
                                                                color="error"
                                                                startIcon={<DeleteIcon />}
                                                                onClick={() => setDeleteMqttOpen(true)}
                                                                disabled={!isOwner(ds)}
                                                            >
                                                                Delete MQTT
                                                            </ActionButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </ButtonGroup>
                                        )}
                                    </Item>
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>

            {/*
              MQTT setup + delete dialogs are only reachable from the
              admin-only ButtonGroup above, so we don't even mount them
              in 'user' mode. Saves a tiny amount of tree work and makes
              the read-only invariant easier to reason about: a user-mode
              row can never enter a state where one of these dialogs is
              open.
            */}
            {isAdminMode && (
                <>
                    <MqttSetupDialog
                        open={mqttDialogOpen}
                        mode={mqttHasSetup ? 'edit' : 'create'}
                        datasetName={ds.DatasetName}
                        existing={mqttHasSetup ? mqttStatus.config : null}
                        helmStatus={mqttHasSetup ? mqttStatus.helm?.status : undefined}
                        onSubmit={handleMqttSubmit}
                        onSuccess={() => {
                            if (typeof onMqttSuccess === 'function') {
                                onMqttSuccess(mqttHasSetup ? 'updated' : 'created', ds);
                            }
                        }}
                        onClose={() => setMqttDialogOpen(false)}
                    />

                    <ConfirmActionDialog
                        open={deleteMqttOpen}
                        title={`Delete MQTT setup for ${ds.DatasetName}?`}
                        description={
                            'This permanently removes the Mosquitto broker, ingester pod and ingester token. ' +
                            'Any clients still connecting to this broker will start failing. ' +
                            'You can re-create the MQTT setup later from this tab — clients will need new credentials.'
                        }
                        confirmLabel="Delete MQTT"
                        // Type-the-dataset-name gate for a high-blast-radius destructive
                        // action (DES2). AsyncFormDialog renders the TextField and
                        // disables the confirm button until the input matches.
                        confirmText={ds.DatasetName}
                        onConfirm={handleMqttDelete}
                        onClose={() => setDeleteMqttOpen(false)}
                    />
                </>
            )}
        </Fragment>
    );
}

AdminDatasetRow.propTypes = {
    ds: PropTypes.shape({
        DatasetID: PropTypes.number.isRequired,
        DatasetName: PropTypes.string.isRequired,
        Organization: PropTypes.string.isRequired,
        Category: PropTypes.string,
        AccessRole: PropTypes.string.isRequired,
        AccessType: PropTypes.string.isRequired,
        ShortInfo: PropTypes.string,
        LongInfo: PropTypes.string,
        Tags: PropTypes.array
    }).isRequired,
    mode: PropTypes.oneOf(['admin', 'user']),
    onEditMetadata: PropTypes.func.isRequired,
    onMqttSuccess: PropTypes.func
};
